import { useEffect, useRef, useState } from 'react';

import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { AuthType } from '../../../graphql.generated';
import { loadGoogleSdk } from '../utils/loadGoogleSdk';
import {
    SocialSignInGoogleV2Document,
    SocialSignInGoogleV2Mutation,
    SocialSignInGoogleV2MutationVariables
} from '../queries/googleSignIn.generated';
import {
    // TODO: Observability replace with RUM solution
    // logAuthSuccess,
    logNetworkAuthFailure,
    logSocialAuthFailure,
    logUserAuthFailure,
    trackAuthFailure,
    trackAuthRememberMe,
    trackAuthSuccess
} from './authMutationHook.tracking';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';
import { getNextBestAction } from '../utils/getNextBestAction';

export const useGoogle: AuthMutationHook<boolean | undefined> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    // we're using local state here since we count on re-renders to refresh the callback
    const [isGoogleLoaded, setisGoogleLoaded] = useState(false);
    const { setCurrentSession } = useAuth();
    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInGoogleV2Mutation,
        SocialSignInGoogleV2MutationVariables
    >(SocialSignInGoogleV2Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const googleCallbackRef = useRef<typeof signInMutation>();

    useEffect(() => {
        googleCallbackRef.current = signInMutation;
    });

    const init = () => {
        loadGoogleSdk();
        setisGoogleLoaded(true);
    };

    const trackError = () => {
        trackAuthFailure({
            source: 'google',
            type: 'signin',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });
    };

    const trackAndThrowError = (message: string, requestId?: string) => {
        trackError();

        const requestIdMessage = requestId ? ` (Request ID: ${requestId})` : '';

        throw new Error(`${message}${requestIdMessage}`);
    };

    const authenticationMutation = async (payload: {
        accessToken: string;
        rememberMe: boolean;
    }) => {
        const response = await googleCallbackRef.current?.(payload);

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'google' });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again. (Network error)'
            );
        }

        if (!response) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.'
            );
        }

        if (!response.data || response.errors) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        const { data } = response;

        if (data.socialSignInGoogleV2?.__typename === 'AuthError') {
            return trackAndThrowError(
                'Something went wrong and you have not been signed in. Please try again.',
                response.extensions?.requestId
            );
        }

        if (!data.socialSignInGoogleV2?.user) {
            return trackAndThrowError(
                'Something went wrong and you have not been signed in. Please try again.',
                response.extensions?.requestId
            );
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (
            !data.socialSignInGoogleV2.user.loggedIn ||
            !data.socialSignInGoogleV2.user.userUuid
        ) {
            logUserAuthFailure({
                type: 'google',
                subtype: !data.socialSignInGoogleV2.user.userUuid
                    ? 'no_uuid'
                    : 'not_logged_in'
            });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again later.',
                response.extensions?.requestId
            );
        }

        const {
            socialSignInGoogleV2: { authType, user }
        } = data;

        const isNewUserAuthentication = authType === AuthType.Register;

        const type = isNewUserAuthentication ? 'registration' : 'signin';

        // TODO: Observability replace with RUM solution
        // logAuthSuccess('google-auth');
        trackAuthSuccess({
            source: 'google',
            type,
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        if (payload?.rememberMe) {
            trackAuthRememberMe({
                source: 'google',
                type,
                userObject: user,
                label: trackingData.label,
                segmentData: trackingData.segmentData
            });
        }

        setCurrentSession(user, isNewUserAuthentication);

        const nextBestActions = getNextBestAction(user);

        return { ...user, actions: nextBestActions };
    };

    const authenticate = async (rememberMe?: boolean) => {
        if (!isGoogleLoaded) {
            init();
        }

        return new Promise<ReturnType<typeof authenticationMutation>>(
            (res, rej) => {
                window.google.accounts?.oauth2
                    .initTokenClient({
                        client_id:
                            '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                        ux_mode: 'popup',
                        scope: 'https://www.googleapis.com/auth/userinfo.email',
                        callback: (response) => {
                            if (response.access_token) {
                                return res(
                                    authenticationMutation({
                                        accessToken: response.access_token,
                                        rememberMe: rememberMe || false
                                    })
                                );
                            } else {
                                logSocialAuthFailure({
                                    name: 'google-auth',
                                    type: 'missing-token'
                                });

                                trackError();

                                return rej(
                                    new Error(
                                        'Something went wrong and you have not been signed in. Please try again.'
                                    )
                                );
                            }
                        },
                        error_callback: (error) => {
                            logSocialAuthFailure({
                                name: 'google-auth',
                                type:
                                    error?.type === 'popup_closed'
                                        ? 'popup_closed_by_user'
                                        : 'immediate_failed'
                            });

                            trackError();

                            return rej(
                                new Error(
                                    'Something went wrong and you have not been signed in. Please try again.'
                                )
                            );
                        }
                    })
                    ?.requestAccessToken?.();
            }
        );
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return authenticate;
};
